import { useState, useEffect, useCallback } from 'preact/hooks'
import { apiClient, InitData, tenantSiteUrl, assetUrl, udLink, treatmentBookingHref } from '../../core'
import { t, formatMoney } from '../../core/i18n'
import { formatDuration } from '../../core/duration'

interface Treatment {
  id: string
  title: string
  description: string | null
  shortDescription?: string | null
  priceDkk: number
  /** Gruppebooking (OPG-0041): prisen er pr. person. */
  pricePerPerson?: boolean
  durationMinutes: number
  imageUrl: string | null
  benefits?: string[] | null
}

interface TreatmentCategory {
  id: string
  name: string
  description: string | null
  color: string | null
  treatments: Treatment[]
}

interface TreatmentsResponse {
  categories: TreatmentCategory[]
}

interface Props {
  initData: InitData
  showPrice?: boolean
  showDuration?: boolean
  showBenefits?: boolean
  title?: string | null
}

/**
 * TreatmentsWidget — mirrors HS-native treatment cards.
 *
 * Card layout:
 *   - 16:9 image at top (cover, no border-radius)
 *   - Title (bold, 1.3rem)
 *   - Short description (1 line truncated, muted)
 *   - Meta line: duration · price
 *   - Description (line-clamp 3)
 *   - Benefits list with orange ✓ (max 4)
 *   - Outlined "Book" CTA → behandlinger page
 */
export function TreatmentsWidget({ initData, showPrice = true, showDuration = true, showBenefits = true, title = null }: Props) {
  // Pulled out here because the render below shadows `t` with its map parameter.
  const str = t(initData.locale)
  const [categories, setCategories] = useState<TreatmentCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadTreatments = useCallback(async () => {
    const result = await apiClient.get<TreatmentsResponse>('/api/widget/treatments')
    if (result.error) {
      setError(result.error)
    } else if (result.data?.categories) {
      setCategories(result.data.categories)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    loadTreatments()
  }, [loadTreatments])

  if (loading) {
    return (
      <div>
        <style>{styles}</style>
        <div class="hsw-tr-grid">
          {[0, 1, 2].map(i => (
            <div key={i} class="hsw-tr-card hsw-tr-skel-card">
              <div class="hsw-tr-skel hsw-tr-skel-img" />
              <div class="hsw-tr-skel-body">
                <div class="hsw-tr-skel hsw-tr-skel-title" />
                <div class="hsw-tr-skel hsw-tr-skel-meta" />
                <div class="hsw-tr-skel hsw-tr-skel-line" />
                <div class="hsw-tr-skel hsw-tr-skel-line" />
                <div class="hsw-tr-skel hsw-tr-skel-line" style={{ width: '70%' }} />
                <div class="hsw-tr-skel hsw-tr-skel-button" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{styles}</style>
        <div class="hsw-tr-error" role="alert">
          <strong>{str.loadTreatmentsError}</strong>
          <div class="hsw-tr-error-msg">{error}</div>
        </div>
      </div>
    )
  }

  const totalTreatments = categories.reduce((sum, cat) => sum + cat.treatments.length, 0)
  if (!totalTreatments) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>💆</div>
        <p>{str.noTreatments}</p>
      </div>
    )
  }

  const siteUrl = tenantSiteUrl(initData)
  const showCategoryHeadings = categories.length > 1

  return (
    <div>
      <style>{styles}</style>
      {title && <h2 class="hsw-tr-title-main">{title}</h2>}
      {categories.map(cat => (
        <div key={cat.id} class="hsw-tr-section">
          {showCategoryHeadings && (
            <h3
              class="hsw-tr-section-title"
              style={{ color: cat.color || 'var(--yoga-primary, #FF6B35)' }}
            >
              {cat.name}
            </h3>
          )}
          <div class="hsw-tr-grid">
            {cat.treatments.map(t => {
              const benefits = (t.benefits ?? []).slice(0, 4)
              // Åbner booking-vinduet for netop denne behandling, ikke den generelle liste.
              const bookingHref = treatmentBookingHref(siteUrl, t.id)
              return (
                <div key={t.id} class="hsw-tr-card">
                  <div class="hsw-tr-img-wrap">
                    {t.imageUrl ? (
                      <img src={assetUrl(t.imageUrl)} alt={t.title} class="hsw-tr-img" loading="lazy" decoding="async" />
                    ) : (
                      <div class="hsw-tr-img-placeholder" aria-hidden="true">💆</div>
                    )}
                  </div>
                  <div class="hsw-tr-body">
                    <h4 class="hsw-tr-title">{t.title}</h4>

                    {t.shortDescription && (
                      <div class="hsw-tr-short">{t.shortDescription}</div>
                    )}

                    {(showDuration || showPrice) && (
                      <div class="hsw-tr-meta">
                        {showDuration && <span>{formatDuration(t.durationMinutes, initData.locale)}</span>}
                        {showDuration && showPrice && <span class="hsw-tr-meta-sep">·</span>}
                        {showPrice && (
                          <span>
                            {t.pricePerPerson
                              ? str.pricePerPerson.replace('{price}', formatMoney(t.priceDkk, initData.locale, initData.currency))
                              : formatMoney(t.priceDkk, initData.locale, initData.currency)}
                          </span>
                        )}
                      </div>
                    )}

                    {t.description && (
                      <p class="hsw-tr-desc">{t.description}</p>
                    )}

                    {showBenefits && benefits.length > 0 && (
                      <ul class="hsw-tr-benefits">
                        {benefits.map((b, i) => (
                          <li key={i}><span class="hsw-tr-check">✓</span>{b}</li>
                        ))}
                      </ul>
                    )}

                    <a href={bookingHref} {...udLink(bookingHref)} class="hsw-tr-cta">{str.book}</a>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

const styles = `
  .hsw-tr-title-main {
    font-size: 2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 2rem;
    max-width: 1200px;
    letter-spacing: -0.01em;
  }
  .hsw-tr-section {
    max-width: 1200px;
    margin: 0 auto 2rem;
  }
  .hsw-tr-section:last-child { margin-bottom: 0; }
  .hsw-tr-section-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 1rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }
  .hsw-tr-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  .hsw-tr-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all .3s;
  }
  .hsw-tr-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    transform: translateY(-2px);
  }
  .hsw-tr-img-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #f5f5f5;
    overflow: hidden;
  }
  .hsw-tr-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 0;
  }
  .hsw-tr-img-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
    background: #f5f5f5;
    color: #d4d4d4;
  }
  .hsw-tr-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .hsw-tr-title {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0 0 0.4rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
    line-height: 1.3;
  }
  .hsw-tr-short {
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #737373);
    margin: 0 0 0.6rem;
    line-height: 1.4;
    /* Studiets egne linjeskift vises (sikker tekst, ingen HTML); højst 2 linjer på kortet. */
    white-space: pre-line;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hsw-tr-meta {
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #525252);
    margin: 0 0 0.85rem;
    font-weight: 500;
    letter-spacing: 0.01em;
  }
  .hsw-tr-meta-sep {
    margin: 0 0.5rem;
    color: var(--yoga-text-muted, #a3a3a3);
  }
  .hsw-tr-desc {
    font-size: 0.9rem;
    color: var(--yoga-text-muted, #525252);
    line-height: 1.55;
    margin: 0 0 1rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .hsw-tr-benefits {
    list-style: none;
    padding: 0;
    margin: 0 0 1.25rem;
  }
  .hsw-tr-benefits li {
    padding: 0.4rem 0 0.4rem 1.5rem;
    position: relative;
    font-size: 0.85rem;
    color: var(--yoga-text, #404040);
    line-height: 1.4;
  }
  .hsw-tr-check {
    position: absolute;
    left: 0;
    color: var(--yoga-primary, #FF6B35);
    font-weight: 700;
    font-size: 0.95rem;
  }
  .hsw-tr-cta {
    display: inline-block;
    margin-top: auto;
    padding: 0.7rem 1.25rem;
    text-align: center;
    background: transparent;
    color: var(--yoga-text, #0a0a0a);
    font-weight: 500;
    border: 1px solid #d4d4d4;
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-size: 0.85rem;
    letter-spacing: 0.01em;
    transition: all .3s;
  }
  .hsw-tr-cta:hover {
    background: var(--yoga-text, #0a0a0a);
    color: var(--yoga-card-bg, #fff);
    border-color: var(--yoga-text, #0a0a0a);
  }
  /* Error */
  .hsw-tr-error {
    max-width: 1200px;
    margin: 0 auto;
    padding: 1rem 1.25rem;
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #991b1b;
    border-radius: var(--yoga-radius, 0);
    font-size: 0.9rem;
    line-height: 1.5;
  }
  .hsw-tr-error-msg {
    margin-top: 0.25rem;
    color: #b91c1c;
    font-size: 0.85rem;
  }
  /* Skeletons */
  .hsw-tr-skel-card { overflow: hidden; }
  .hsw-tr-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    border-radius: 4px;
  }
  .hsw-tr-skel-img {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 0;
  }
  .hsw-tr-skel-body {
    padding: 1.5rem;
  }
  .hsw-tr-skel-title { height: 22px; width: 65%; margin-bottom: 0.75rem; }
  .hsw-tr-skel-meta { height: 12px; width: 45%; margin-bottom: 1rem; }
  .hsw-tr-skel-line { height: 14px; width: 100%; margin-bottom: 0.5rem; }
  .hsw-tr-skel-button { height: 38px; width: 100%; margin-top: 1rem; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`
